/**
 * Tipos canônicos do domínio TenantChannelConfig (Fatia 9 / ADR-135).
 *
 * Configuração de canal por tenant. `config` é polimórfico discriminado por
 * `channel` — cada canal define seu próprio shape. Engine valida via Zod no
 * boundary (controller); shared expõe apenas tipos.
 *
 * **Credentials sensíveis** (accessToken, webhookVerifyToken se per-tenant)
 * **NÃO** ficam em `config` — vivem no Vault em produção (`secret/lume/
 * {tenantId}/{channel}/access_token`) e `.env` em dev.
 *
 * v1: apenas `whatsapp-cloud-api` materializado. Outros canais (webchat,
 * email, telegram, instagram-dm) ganham config shape conforme habilitação.
 */
export * from './media-rules';
// Fase 2 «Conduta e Contexto» §C.5 — a CLASSE do canal (nível 5 dos 6 níveis).
export * from './channel-families';
// Spec `2026-08-18-webchat-do-cliente-tres-cenarios` §5.3 — durável × sessão.
export * from './channel-durability';
// Elegibilidade para conversa proativa — fonte ÚNICA do DTO do engine e do adapter do front.
export * from './proactive-eligibility';
/**
 * O interruptor do canal — e **só** o interruptor (Item 505, decidido na F2 em
 * 19/Set/2026).
 *
 * Tinha também `error` e `pending-verification`. Os dois prometiam um estado de
 * verificação que não existia em lugar nenhum; hoje ele existe e mora na SAÚDE do canal
 * (`GET /v1/tenant-channel-configs/:id/health`). Duas fontes respondendo «este canal está
 * de pé?» é uma cópia que envelhece — e foi uma cópia dessas que manteve o incidente de
 * 15/Set/2026 invisível por dias.
 */
export const CHANNEL_CONFIG_STATUSES = ['active', 'inactive'];
/**
 * Canais que possuem **adapter real** (event-driven outbound service)
 * materializado no Engine. Single source of truth canonical.
 *
 * **Consumido por** `MockOutboundService` para decidir se deve simular
 * delivery (setTimeout → `delivered`/`read`). Canais nesta lista NÃO são
 * mockados — o adapter real cuida do despacho + status.
 *
 * **Adicionar canal aqui** ao materializar um `*OutboundService` que consome
 * `MESSAGE_SENT` e despacha para API externa. Caso contrário, o mock vai
 * disparar em paralelo e sobrescrever silenciosamente o status real
 * (bug histórico — falso-positivo de delivery, 28/Mai/2026).
 *
 * Histórico:
 *   - 'whatsapp-cloud-api': Sprint Fatia 9 (Brief 9 V2).
 *   - 'telegram': Sprint Conversa Real Telegram (Fatia 8.bis Telegram).
 *
 * Pendentes (ainda mockados):
 *   - 'webchat': cliente Webchat mock client harness v1 (Item 270);
 *     futuro lar do cliente comercial Webchat.
 *   - 'email', 'instagram-dm': sem adapter real materializado.
 */
// Tipo intermediário força exhaustive check do `ChannelKind` na construção
// (qualquer string fora do tipo gera compile error aqui), mas o Set público
// é tipado como `ReadonlySet<string>` para permitir `.has(channel)` quando
// o caller só tem o channel como string (ex.: payload de evento WS).
const REAL_ADAPTER_CHANNELS = ['whatsapp-cloud-api', 'telegram'];
export const CHANNELS_WITH_REAL_ADAPTER = new Set(REAL_ADAPTER_CHANNELS);
//# sourceMappingURL=index.js.map